import React, { useEffect } from 'react';

import { type PublicPluginAPI } from '@atlaskit/editor-common/types';
import { usePluginStateEffect } from '@atlaskit/editor-common/use-plugin-state-effect';
import { type FocusPlugin } from '@atlaskit/editor-plugin-focus';
import { Fragment, type Node } from '@atlaskit/editor-prosemirror/model';
import type { Selection } from '@atlaskit/editor-prosemirror/state';
import type { DecorationSet, EditorView } from '@atlaskit/editor-prosemirror/view';
import { type MentionNameDetails } from '@atlaskit/mention';
import { usePublish } from '@atlaskit/rovo-triggers/main';
import { expValEquals } from '@atlaskit/tmp-editor-statsig/exp-val-equals';
import { convertProsemirrorNodeToMarkdown } from '@atlassian/ai-model-io/convert-prosemirror-to-markdown';
import { getMentionMap } from '@atlassian/ai-model-io/utils/mention-map';

import type { AIPlugin } from '../../../aiPluginType';
import { getPluginState } from '../../../pm-plugins/decoration/decoration-plugin-factory';
import { updatePublish } from '../../../pm-plugins/rovo-agents/commands';
import { type EditorPluginAIProvider } from '../../../types/types';
import { getAIHighlightPositions } from '../../utils/selection';

export const getSelectionContent = ({
	modalDecorationSet,
	doc,
}: {
	doc: Node;
	modalDecorationSet: DecorationSet;
}): Fragment | undefined => {
	const positions = getAIHighlightPositions({ modalDecorationSet });
	return positions ? doc.slice(positions[0], positions[1], true).content : undefined;
};

type PublishArgs = {
	doc?: Node;
	selection?: Selection;
};

/**
 *  This component is required so we can call the usePublish hook
 *  and store the publish function in the rovo-agents plugin on mount.
 */
export const PublishToRovo = ({
	editorView,
	getMentionNameDetails,
	editorPluginAIProvider,
	editorApi,
}: {
	editorApi: PublicPluginAPI<[FocusPlugin, AIPlugin]> | undefined;
	editorPluginAIProvider: EditorPluginAIProvider;
	editorView: EditorView;
	getMentionNameDetails?: (id: string) => Promise<MentionNameDetails | undefined>;
}): React.JSX.Element => {
	const publish = usePublish('ai-mate');
	const { product } = editorPluginAIProvider;

	const publishDocAndSelection = React.useCallback(
		async ({ doc, selection }: PublishArgs) => {
			// This is used as performance optimization since the publish function is called very often
			// and we don't want to block the main thread for this operation.
			// requestIdleCallback is not supported by all browsers, so we fallback to requestAnimationFrame
			const queueTask = window.requestIdleCallback || window.requestAnimationFrame;

			if (!doc || !selection) {
				queueTask(() => {
					publish({
						type: 'editor-context-payload',
						source: 'editor',
						product,
						data: undefined,
					});
				});
				return;
			}

			// Retrieve the modal decoration set from the plugin state to access the selection state,
			// since the editor's selection is cleared when the modal opens.
			const { modalDecorationSet } = getPluginState(editorView.state);

			// Initially, assume the selected content from editorView will be used
			let selectionContent = selection.content().content;

			const selectionContentFromDecorations = getSelectionContent({ modalDecorationSet, doc });

			if (selectionContentFromDecorations) {
				selectionContent = selectionContentFromDecorations;
			}

			let mentionMap = {};
			if (getMentionNameDetails) {
				mentionMap = await getMentionMap({ node: doc, getMentionNameDetails });
			}

			const { markdown: selectionMD } = convertProsemirrorNodeToMarkdown({
				node: Fragment.from(selectionContent),
				featureToggles: {
					markdownPlus: false,
				},
				mentionMap,
			});

			queueTask(() => {
				publish({
					type: 'editor-context-payload',
					source: 'editor',
					product,
					data: {
						document: { type: 'text/adf', content: doc.toJSON() },
						selection: { type: 'text/markdown', content: selectionMD },
					},
				});
			});
		},
		[editorView, getMentionNameDetails, product, publish],
	);

	usePluginStateEffect(editorApi, ['focus'], ({ focusState }) => {
		// this is the same as onBlur
		if (!focusState?.hasFocus) {
			publishDocAndSelection({
				doc: editorView.state.doc,
				selection: editorView.state.selection,
			});
		}
	});

	usePluginStateEffect(editorApi, ['aiExperience'], ({ aiExperienceState }) => {
		// When text is selected or deselected, publish the updated selection context to Rovo
		if ((aiExperienceState?.nonCursorSelectionChangeCount ?? 0) >= 1) {
			publishDocAndSelection({
				doc: editorView.state.doc,
				selection: editorView.state.selection,
			});
		}
	});

	useEffect(() => {
		return () => {
			publish({
				type: 'editor-context-payload',
				source: 'editor',
				product,
				data: undefined,
			});
		};
	}, [publish, product]);

	useEffect(() => {
		if (expValEquals('platform_editor_fix_editor_unhandled_type_errors', 'isEnabled', true)) {
			updatePublish(publish)(editorView.state, editorView.dispatch);
		}
	}, [publish, editorView]);

	if (!expValEquals('platform_editor_fix_editor_unhandled_type_errors', 'isEnabled', true)) {
		updatePublish(publish)(editorView.state, editorView.dispatch);
	}

	return <></>;
};
